import { AuthContext } from '@repo-level/core-front';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { buildChannelName } from './channelName';
import { CentrifugoContext } from './context';
import { centrifugoService } from './service';
import { ChannelType, ConnectionStatus } from './types';

export const useSubscription = <T>(
  channel: string | null,
  onMessage: (data: T) => void,
  channelType: ChannelType = ChannelType.Private,
  namespace?: string
) => {
  const { settings, status } = useContext(CentrifugoContext);
  const { isAuthenticated } = useContext(AuthContext);

  const clientName = settings?.clientName;
  const user = isAuthenticated ? settings?.user : undefined;

  // Kept in a ref so an inline callback does not resubscribe on every render
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const subscriptionChannel = useMemo(
    () =>
      buildChannelName({
        channel,
        clientName,
        user,
        channelType,
        namespace,
      }),
    [channel, channelType, clientName, user, namespace]
  );

  useEffect(() => {
    if (!subscriptionChannel || status !== ConnectionStatus.Connected) return;

    centrifugoService.subscribe<T>(subscriptionChannel, (data) => {
      onMessageRef.current(data);
    });

    return () => {
      centrifugoService.unsubscribe(subscriptionChannel);
    };
  }, [subscriptionChannel, status]);
};
